import { Check, Lock, Trash2, Undo2 } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";

import { timeActions } from "../actions";
import { FlatFeeTag, NarrativeEditor } from "../board/BoardCard";
import { ActivityList } from "../components/ActivityList";
import { CandidateList } from "../components/CandidateList";
import { CaseDot, CasePicker } from "../components/CasePicker";
import { CodeSelect } from "../components/CodeSelect";
import { HoursStepper } from "../components/HoursStepper";
import { clockRange, hourOf, releaseBlock } from "../model";
import { useNarrative } from "../narrativeDrafts";
import type { TimeEntry } from "../types";
import { formatDuration, formatHours, isUnreleased } from "../utils";

/** The selected entry, editable while it's yours and unreleased. */
export const EntryPanel: FC<{ entry: TimeEntry; onDone?: () => void }> = ({
  entry,
  onDone
}) => {
  const open = isUnreleased(entry.status);
  const narrative = useNarrative(entry);
  const block = releaseBlock({ ...entry, narrative });
  const start = hourOf(entry.started_at);
  const end = hourOf(entry.ended_at);
  return (
    <>
      <div
        className="flex flex-1 flex-col gap-4 overflow-y-auto p-5"
        data-testid="entry-panel"
      >
        <span className="flex flex-col gap-1">
          <span className="flex items-center gap-2">
            {entry.individual_id ? (
              <>
                <CaseDot id={entry.individual_id} />
                <span className="truncate text-sm font-medium">
                  {entry.case_label}
                </span>
              </>
            ) : (
              <span className="text-sm font-medium text-warn">
                Which case is this?
              </span>
            )}
            <span className="flex-1" />
            {entry.individual_id ? <FlatFeeTag entry={entry} /> : null}
            {open ? null : (
              <span className="text-sm text-fg-3">{entry.status_label}</span>
            )}
          </span>
          <span className="text-sm text-fg-3 tabular-nums">
            {start !== null && end !== null
              ? clockRange(start, end)
              : entry.work_date}
            {entry.origin === "captured" && entry.duration_seconds_raw
              ? ` · ${formatDuration(entry.duration_seconds_raw)} captured`
              : ""}
          </span>
        </span>

        {open && !entry.individual_id && entry.candidates.length ? (
          <CandidateList
            candidates={entry.candidates}
            onPick={(c) => void timeActions.assign(entry.id, c.individual_id)}
          />
        ) : null}

        {open ? (
          <span className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-3">Case</span>
            <CasePicker
              value={entry.individual_id}
              label={entry.case_label}
              className="w-full"
              onPick={(c) => void timeActions.assign(entry.id, c.individual_id)}
            />
          </span>
        ) : null}

        <span className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg-3">Narrative</span>
          <NarrativeEditor
            key={entry.id}
            entry={entry}
            rows={4}
            locked={!open}
          />
        </span>

        <span className="grid grid-cols-2 gap-4">
          <span className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-3">Hours</span>
            <span>
              <HoursStepper
                value={entry.duration_hours}
                locked={!open}
                onChange={(h) =>
                  void timeActions.update(entry.id, { duration_hours: h })
                }
              />
            </span>
          </span>
          <span className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-3">Activity code</span>
            <CodeSelect
              className="w-full min-w-0"
              value={entry.utbms_activity}
              disabled={!open}
              onChange={(code) =>
                void timeActions.update(entry.id, { utbms_activity: code })
              }
            />
          </span>
        </span>

        {entry.write_down ? (
          <span className="text-sm text-fg-3">
            Written down from {formatHours(entry.write_down.captured_hours)}
            {entry.write_down.note ? `: ${entry.write_down.note}` : ""}
          </span>
        ) : null}

        {entry.activity?.length ? (
          <span className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-3">Activity</span>
            <ActivityList rows={entry.activity} max={20} />
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2 border-t border-line-2 px-5 py-3">
        {open ? (
          <>
            <Button
              variant="primary"
              disabled={Boolean(block)}
              onClick={async () => {
                if (await timeActions.release([entry])) onDone?.();
              }}
            >
              <Check /> Release
            </Button>
            {block ? <span className="text-sm text-fg-3">{block}</span> : null}
            <span className="flex-1" />
            <Button
              variant="ghost"
              onClick={async () => {
                if (await timeActions.discard([entry.id])) onDone?.();
              }}
            >
              <Trash2 /> Discard
            </Button>
          </>
        ) : entry.status === "released" ? (
          <>
            <span className="text-sm text-fg-3">
              Waiting for billing review
            </span>
            <span className="flex-1" />
            <Button
              variant="secondary"
              onClick={() => void timeActions.recall([entry.id])}
            >
              <Undo2 /> Recall
            </Button>
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm text-fg-3">
            <Lock className="size-3" /> {entry.status_label}. Ask an admin if it
            needs changes.
          </span>
        )}
      </div>
    </>
  );
};
